import { useEffect, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useBeforeUnload,
  useBlocker,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useNaves, useGuardarSensoresNave } from '@/lib/queries/naves';
import { useSensoresNave } from '@/lib/queries/iot';
import { getSensorEquipment, type SensorEquipment } from '@/lib/sensorEquipment';
import { useAuthStore } from '@/stores/auth';
import { useEntityId } from '@/lib/offline/operations';
import { currentBundle } from '@/lib/offline/bundle';
import { extractApiMessage } from '@/lib/api';
import { ModalOverlay } from '@/components/ModalOverlay';
import { useToast } from '@/components/toastStore';
import { AppHeaderContent } from '@/components/AppHeaderContent';
import { NaveSettingsTabs } from '@/components/NaveSettingsTabs';
import type { Nave } from '@/lib/types';

export function AjustesSensoresAliasPage() {
  const [params] = useSearchParams();
  const id = params.get('nave');
  return (
    <Navigate
      replace
      to={id && /^-?[1-9]\d*$/.test(id) ? `/ajustes/naves/${id}/sensores` : '/ajustes/naves'}
    />
  );
}

export function AjustesSensoresPage() {
  const { naveId: rawId } = useParams<{ naveId: string }>();
  const naveId = useEntityId(rawId, 'naves');
  const owner = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.rol);
  const canRead = role === 'admin' || role === 'ganadero' || role === 'veterinario';
  const q = useNaves();
  const nave = q.data?.find((n) => n.id === naveId);
  if (canRead && nave) return <NaveEquipmentForm key={`${owner}:${rawId}`} nave={nave} />;
  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent backTo="/ajustes/naves" backLabel="Naves" />
      </header>
      <h1 className="ui-page-title">Sensores</h1>
      {!canRead ? (
        <p role="alert">No tienes permiso para consultar los sensores.</p>
      ) : q.isLoading ? (
        <p role="status">Cargando nave…</p>
      ) : q.isError ? (
        <div role="alert" className="parte-error">
          <p>{extractApiMessage(q.error, 'No se pudieron cargar las naves.')}</p>
          <button type="button" className="ui-secondary mt-3" onClick={() => void q.refetch()}>
            Reintentar
          </button>
        </div>
      ) : (
        <p role="alert">Nave no encontrada.</p>
      )}
    </main>
  );
}

function NaveEquipmentForm({ nave }: { nave: Nave }) {
  const owner = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.user?.rol);
  const canWrite = role === 'admin' || role === 'ganadero';
  const sensors = useSensoresNave(nave.id);
  const guardar = useGuardarSensoresNave();
  const toast = useToast();
  const known = nave.sensor_equipment != null || sensors.data != null;
  const source = known ? getSensorEquipment(nave, sensors.data) : null;
  const serialized = JSON.stringify(source);
  const [equipment, setEquipment] = useState<SensorEquipment | null>(source);
  const baseline = useRef(serialized);
  const initialOwner = useRef(owner);
  const knownChoices = useRef(new Set<string>(['A', 'B', 'C']));
  for (const station of [
    ...(source?.environment ?? []),
    ...(equipment?.environment ?? []),
    ...(sensors.data?.available_environment ?? []),
  ])
    knownChoices.current.add(station);
  const [busy, setBusy] = useState(false),
    [confirming, setConfirming] = useState(false),
    [error, setError] = useState<string | null>(null);
  const localPending = nave.sensor_equipment_local_pending === true;
  const mounted = useRef(false),
    sending = useRef(false),
    dirty = useRef(false),
    errorRef = useRef<HTMLDivElement>(null);
  dirty.current = !!equipment && JSON.stringify(equipment) !== baseline.current;
  const blocker = useBlocker(() => dirty.current || sending.current);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  const farm = useRef(currentBundle()?.explotacion_id);
  const current = () =>
    mounted.current &&
    useAuthStore.getState().user?.id === initialOwner.current &&
    useAuthStore.getState().user?.rol === role &&
    (farm.current == null || currentBundle()?.explotacion_id === farm.current);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!dirty.current && !sending.current) {
      baseline.current = serialized;
      setEquipment(JSON.parse(serialized) as SensorEquipment | null);
    }
  }, [serialized]);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  useBeforeUnload((event) => {
    if (dirty.current || sending.current) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  const choices = Array.from(knownChoices.current);
  function change(next: SensorEquipment) {
    if (!canWrite || sending.current || !current()) return;
    setError(null);
    setEquipment(next);
  }
  async function save(confirmed = false) {
    if (!canWrite || sending.current || !equipment || !dirty.current || !known || !current())
      return;
    const previous = JSON.parse(baseline.current) as SensorEquipment;
    const removed =
      previous.environment.some((station) => !equipment.environment.includes(station)) ||
      (['ammonia', 'water', 'silo'] as const).some((key) => previous[key] && !equipment[key]);
    if (removed && !confirmed) {
      setConfirming(true);
      return;
    }
    sending.current = true;
    setBusy(true);
    setConfirming(false);
    setError(null);
    try {
      const saved = await guardar.mutateAsync({
        id: nave.id,
        equipment,
        confirm_deactivation: removed && confirmed,
      });
      if (!current()) return;
      const accepted = getSensorEquipment(saved, sensors.data);
      baseline.current = JSON.stringify(accepted);
      dirty.current = false;
      setEquipment(accepted);
      if (blockerRef.current.state === 'blocked') blockerRef.current.reset();
      toast.show({ kind: 'success', message: 'Sensores guardados.' });
    } catch (err) {
      if (current()) setError(extractApiMessage(err));
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent backTo="/ajustes/naves" backLabel="Naves" />
      </header>
      <h1 className="ui-page-title">{nave.nombre || nave.codigo}</h1>
      <NaveSettingsTabs naveId={nave.id} active="sensores" />
      {localPending && (
        <p className="cm-form-note" role="status">
          Configuración guardada en este dispositivo. La supervisión del servidor cambiará tras su
          confirmación.
        </p>
      )}
      {!canWrite && <p className="cm-form-note">Tu perfil permite consultar el equipamiento.</p>}
      {error && (
        <div role="alert" tabIndex={-1} ref={errorRef} className="parte-error mb-3">
          {error}
        </div>
      )}
      {!equipment ? (
        <p role={sensors.isError ? 'alert' : 'status'}>
          {sensors.isError
            ? 'Equipamiento no disponible. Conecta para actualizar.'
            : 'Consultando equipamiento…'}
        </p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <fieldset disabled={busy || !canWrite} className="divide-y divide-border">
            <legend className="mb-2 font-medium">Equipamiento instalado</legend>
            {choices.map((station) => (
              <label key={station} className="flex min-h-12 cursor-pointer items-center gap-3 py-3">
                <input
                  type="checkbox"
                  className="size-5 accent-accent"
                  checked={equipment.environment.includes(station)}
                  onChange={(event) =>
                    change({
                      ...equipment,
                      environment: event.target.checked
                        ? choices.filter(
                            (item) => item === station || equipment.environment.includes(item),
                          )
                        : equipment.environment.filter((item) => item !== station),
                    })
                  }
                />
                <span>
                  Estación {station}
                  <small className="block text-text-secondary">Temperatura y humedad</small>
                </span>
              </label>
            ))}
            {(
              [
                { key: 'ammonia', label: 'Amoníaco' },
                { key: 'water', label: 'Agua' },
                { key: 'silo', label: 'Silo' },
              ] as const
            ).map(({ key, label }) => (
              <label key={key} className="flex min-h-12 cursor-pointer items-center gap-3 py-3">
                <input
                  type="checkbox"
                  className="size-5 accent-accent"
                  checked={equipment[key]}
                  onChange={(event) => change({ ...equipment, [key]: event.target.checked })}
                />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>
          <p className="cm-form-note my-3">
            Marca los sensores instalados en esta nave. Al retirar uno, se conserva su histórico.
          </p>
          <footer className="cm-form-actions">
            <Link to="/ajustes/naves" className="ui-secondary">
              Cancelar
            </Link>
            <button
              type="submit"
              className="ui-primary"
              disabled={busy || !canWrite || !dirty.current || !known}
            >
              {busy ? 'Guardando…' : 'Guardar sensores'}
            </button>
          </footer>
        </form>
      )}
      {confirming && (
        <ModalOverlay
          ariaLabel="Retirar supervisión"
          placement="bottom"
          onBackdrop={() => setConfirming(false)}
        >
          <h2 className="ui-page-title">Retirar supervisión</h2>
          <p className="py-3">
            El equipamiento retirado dejará de supervisarse; el histórico se conserva. Si guardas
            sin conexión, la supervisión del servidor cambiará tras confirmar la sincronización.
          </p>
          <button
            type="button"
            className="ui-secondary w-full"
            onClick={() => setConfirming(false)}
          >
            Seguir editando
          </button>
          <button type="button" className="ui-primary mt-2 w-full" onClick={() => void save(true)}>
            Confirmar retirada
          </button>
        </ModalOverlay>
      )}
      {blocker.state === 'blocked' && (
        <ModalOverlay
          ariaLabel="Cambios sin guardar"
          placement="bottom"
          onBackdrop={() => blocker.reset()}
        >
          <h2 className="ui-page-title">{busy ? 'Guardando…' : 'Cambios sin guardar'}</h2>
          <p className="py-3">
            {busy
              ? 'Espera a que termine el guardado antes de salir.'
              : 'Puedes seguir editando o salir sin guardar este borrador.'}
          </p>
          <button type="button" className="ui-primary w-full" onClick={() => blocker.reset()}>
            Seguir editando
          </button>
          {!busy && (
            <button
              type="button"
              className="ui-secondary mt-2 w-full"
              onClick={() => {
                dirty.current = false;
                blocker.proceed();
              }}
            >
              Salir sin guardar
            </button>
          )}
        </ModalOverlay>
      )}
    </main>
  );
}
